import { z } from 'zod';
// ===== Enums =====
export const UserRole = z.enum(['admin', 'project_manager', 'support_staff', 'client']);
export const ProjectStage = z.enum(['enquiry', 'quote', 'design', 'develop', 'review', 'launch', 'finalised']);
export const ProjectStatus = z.enum(['not_started', 'in_progress', 'completed', 'on_hold', 'blocked']);
export const TaskStatus = z.enum(['todo', 'in_progress', 'review', 'done']);
export const TaskPriority = z.enum(['low', 'medium', 'high', 'urgent']);
export const TicketType = z.enum(['bug', 'change_request', 'general_inquiry', 'approval_request']);
export const TicketStatus = z.enum(['open', 'in_progress', 'resolved', 'closed']);
export const InvoiceStatus = z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled', 'awaiting_transfer', 'refunded']);
export const DepositType = z.enum(['none', 'percentage', 'fixed']);
export const PaymentMethod = z.enum(['stripe', 'bank_transfer']);
export const LeadStatus = z.enum(['new', 'contacted', 'meeting_scheduled', 'quote_sent', 'negotiation', 'won', 'lost']);
export const QuoteStatus = z.enum(['draft', 'sent', 'approved', 'revisions_requested', 'accepted', 'converted', 'expired']);
// ===== Auth Schemas =====
export const LoginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
});
export const RegisterSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    name: z.string().min(1, 'Name is required'),
});
// ===== User Schemas =====
export const UserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: UserRole,
    avatar: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    isActive: z.boolean().default(true),
    googleId: z.string().nullable().optional(),
    twoFactorEnabled: z.boolean().default(false),
    hasCompletedOnboarding: z.boolean().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateUserSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1),
    role: UserRole,
    phone: z.string().optional(),
});
export const UpdateUserSchema = CreateUserSchema.partial();
// ===== Client Schemas =====
export const ClientSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    companyName: z.string(),
    phone: z.string().nullable().optional(),
    billingAddress: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateClientSchema = z.object({
    userId: z.string().uuid(),
    companyName: z.string().min(1),
    phone: z.string().optional(),
    billingAddress: z.string().optional(),
    notes: z.string().optional(),
});
export const UpdateClientSchema = CreateClientSchema.partial();
// ===== Project Schemas =====
export const ProjectSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable().optional(),
    clientId: z.string().uuid(),
    pmId: z.string().uuid().nullable().optional(),
    stage: ProjectStage,
    status: ProjectStatus,
    startDate: z.date().nullable().optional(),
    targetDate: z.date().nullable().optional(),
    budget: z.number().nullable().optional(),
    templateId: z.string().uuid().nullable().optional(),
    tags: z.string().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateProjectSchema = z.object({
    name: z.string().min(1, 'Project name is required'),
    description: z.string().optional(),
    clientId: z.string().uuid(),
    pmId: z.string().uuid().optional(),
    stage: ProjectStage.default('enquiry'),
    startDate: z.string().optional(),
    targetDate: z.string().optional(),
    budget: z.number().positive().optional(),
    templateId: z.string().uuid().optional(),
    tags: z.string().optional(),
});
export const UpdateProjectSchema = z.object({
    name: z.string().min(1).optional(),
    description: z.string().optional(),
    clientId: z.string().uuid().optional(),
    pmId: z.string().uuid().optional(),
    stage: ProjectStage.optional(),
    status: ProjectStatus.optional(),
    startDate: z.string().optional(),
    targetDate: z.string().optional(),
    budget: z.number().positive().optional(),
    templateId: z.string().uuid().optional(),
    tags: z.string().optional(),
});
// ===== Task Schemas =====
export const TaskSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    parentId: z.string().uuid().nullable().optional(),
    title: z.string(),
    description: z.string().nullable().optional(),
    assigneeId: z.string().uuid().nullable().optional(),
    dueDate: z.date().nullable().optional(),
    priority: TaskPriority.default('medium'),
    status: TaskStatus.default('todo'),
    estimatedHours: z.number().positive().nullable().optional(),
    sortOrder: z.number().default(0),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// Recurring rule for tasks
const RecurringFreq = z.enum(['daily', 'weekly', 'monthly']);
export const RecurringRuleSchema = z.object({
    freq: RecurringFreq,
    interval: z.number().int().positive().default(1),
    dayOfWeek: z.number().int().min(0).max(6).optional(), // 0=Sun, 1=Mon ...
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    endDate: z.string().optional(),
    count: z.number().int().positive().optional(),
});
export const CreateTaskSchema = z.object({
    projectId: z.string().uuid(),
    parentId: z.string().uuid().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    assigneeId: z.string().uuid().optional(),
    dueDate: z.string().optional(),
    priority: TaskPriority.default('medium'),
    estimatedHours: z.number().positive().optional(),
    isRecurring: z.boolean().optional().default(false),
    recurringRule: RecurringRuleSchema.optional(),
});
export const UpdateTaskSchema = CreateTaskSchema.partial();
// ===== Time Entry Schemas =====
export const TimeEntrySchema = z.object({
    id: z.string().uuid(),
    taskId: z.string().uuid(),
    userId: z.string().uuid(),
    date: z.date(),
    hours: z.number().positive(),
    description: z.string().nullable().optional(),
    billable: z.boolean().default(true),
    approved: z.boolean().default(false),
    createdAt: z.date(),
});
// ===== Ticket Schemas =====
export const TicketSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    submittedById: z.string().uuid(),
    assignedToId: z.string().uuid().nullable().optional(),
    type: TicketType,
    subject: z.string(),
    description: z.string(),
    status: TicketStatus.default('open'),
    priority: TaskPriority.default('medium'),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateTicketSchema = z.object({
    projectId: z.string().uuid(),
    type: TicketType,
    subject: z.string().min(1, 'Subject is required'),
    description: z.string().min(1, 'Description is required'),
    priority: TaskPriority.optional().default('medium'),
    assignedToId: z.string().uuid().optional(),
});
export const UpdateTicketSchema = z.object({
    type: TicketType.optional(),
    subject: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    status: TicketStatus.optional(),
    priority: TaskPriority.optional(),
    assignedToId: z.string().uuid().nullable().optional(),
});
export const CreateTicketCommentSchema = z.object({
    content: z.string().min(1, 'Comment content is required'),
    isInternal: z.boolean().optional().default(false),
});
// ===== Invoice Schemas =====
export const InvoiceLineItemSchema = z.object({
    description: z.string(),
    quantity: z.number().positive(),
    unitPrice: z.number().positive(),
    total: z.number(),
});
export const CreateInvoiceSchema = z.object({
    projectId: z.string().uuid(),
    clientId: z.string().uuid(),
    quoteId: z.string().uuid().optional(),
    lineItems: z.array(InvoiceLineItemSchema).min(1),
    tax: z.number().min(0).default(0),
    discount: z.number().min(0).default(0),
    dueDate: z.string().optional(),
    status: InvoiceStatus.optional().default('draft'),
    // Per-invoice bank transfer override (optional — falls back to global settings)
    bankAccountName: z.string().optional(),
    bankSortCode: z.string().optional(),
    bankAccountNumber: z.string().optional(),
    bankPaymentInstructions: z.string().optional(),
});
// ===== Lead Schemas =====
export const LeadSchema = z.object({
    id: z.string().uuid(),
    companyName: z.string(),
    contactName: z.string(),
    email: z.string().email(),
    phone: z.string().nullable().optional(),
    serviceInterest: z.string().nullable().optional(),
    budgetRange: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    status: LeadStatus.default('new'),
    assignedToId: z.string().uuid().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateLeadSchema = z.object({
    companyName: z.string().min(1, 'Company name is required'),
    contactName: z.string().min(1, 'Contact name is required'),
    email: z.string().email('Valid email is required'),
    phone: z.string().optional(),
    serviceInterest: z.string().optional(),
    budgetRange: z.string().optional(),
    source: z.string().optional(),
    notes: z.string().optional(),
    assignedToId: z.string().uuid().optional(),
});
export const UpdateLeadSchema = z.object({
    companyName: z.string().min(1).optional(),
    contactName: z.string().min(1).optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    serviceInterest: z.string().optional(),
    budgetRange: z.string().optional(),
    source: z.string().optional(),
    notes: z.string().optional(),
    status: LeadStatus.optional(),
    assignedToId: z.string().uuid().nullable().optional(),
});
// ===== Quote Schemas =====
export const QuoteLineItemSchema = z.object({
    description: z.string(),
    quantity: z.number().positive(),
    unitPrice: z.number().positive(),
    // Optional — the server derives total from quantity × unitPrice and ignores the
    // submitted value, so clients (and tests) may omit it.
    total: z.number().optional(),
});
export const CreateQuoteSchema = z.object({
    leadId: z.string().uuid(),
    lineItems: z.array(QuoteLineItemSchema).min(1),
    notes: z.string().optional(),
    validUntil: z.string().optional(),
    // Deposit — explicit choice on every quote (no silent default)
    depositType: DepositType.default('none'),
    depositValue: z.number().min(0).optional(),
    autoSendDepositInvoice: z.boolean().optional().default(false),
});
/** Response shape of a Quote (DB row + relations). */
export const QuoteSchema = z.object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    projectId: z.string().uuid().nullable().optional(),
    quoteNumber: z.string(),
    status: QuoteStatus,
    previousStatus: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    validUntil: z.date().nullable().optional(),
    depositType: DepositType.default('none'),
    depositValue: z.number().nullable().optional(),
    autoSendDepositInvoice: z.boolean().default(false),
    acceptedTermsVersion: z.string().nullable().optional(),
    acceptedTermsAt: z.date().nullable().optional(),
    acceptedTermsByUserId: z.string().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateTimeEntrySchema = z.object({
    taskId: z.string().uuid(),
    date: z.string(),
    hours: z.number().positive('Hours must be positive'),
    description: z.string().optional(),
    billable: z.boolean().optional().default(true),
});
export const UpdateTimeEntrySchema = z.object({
    taskId: z.string().uuid().optional(),
    date: z.string().optional(),
    hours: z.number().positive().optional(),
    description: z.string().optional(),
    billable: z.boolean().optional(),
    approved: z.boolean().optional(),
});
// ===== Notification Schemas =====
export const NotificationSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    type: z.string(),
    title: z.string(),
    message: z.string(),
    link: z.string().nullable().optional(),
    isRead: z.boolean().default(false),
    createdAt: z.date(),
});
// ===== Notification Preference Schemas =====
export const NOTIF_EVENT_TYPES = [
    'task_assigned',
    'status_change',
    'new_comment',
    'new_ticket',
    'ticket_update',
    'file_uploaded',
    'invoice_generated',
    'invoice_paid',
    'time_approved',
    'member_added',
    'quote_invite',
    'user_invite',
    'quote_reminder',
    'quote_declined',
    'quote_changes',
    'quote_expired',
    'quote_accepted',
    'quote_received',
    'broadcast',
    'invoice_reminder',
    'bank_transfer_notified',
    'deposit_invoice',
];
const NotifEventTypeEnum = z.enum(NOTIF_EVENT_TYPES);
export const NotificationPreferenceSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    type: NotifEventTypeEnum,
    email: z.boolean().default(true),
    inApp: z.boolean().default(true),
});
export const UpdateNotificationPreferencesSchema = z.object({
    preferences: z.array(z.object({
        type: NotifEventTypeEnum,
        email: z.boolean(),
        inApp: z.boolean(),
    })),
});
// ===== API Response Schemas =====
export const ApiResponseSchema = (data) => z.object({
    success: z.boolean(),
    data: data.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
});
// ===== Invitation Schemas =====
export const InviteType = z.enum(['user_invite', 'quote_invite']);
export const CreateInvitationSchema = z.object({
    email: z.string().email('Valid email is required'),
    name: z.string().min(1, 'Name is required'),
    role: UserRole,
});
export const AcceptInviteSchema = z.object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    acceptedTerms: z.literal(true, { errorMap: () => ({ message: 'You must agree to the Terms & Conditions to accept this quote.' }) }).optional(),
});
export const DeclineQuoteSchema = z.object({
    action: z.enum(['decline', 'request_changes']),
    reason: z.string().optional(),
});
export const PaginationSchema = z.object({
    page: z.number().int().positive().default(1),
    limit: z.number().int().positive().max(100).default(20),
    total: z.number().int().min(0),
});
//# sourceMappingURL=index.js.map